import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { useAuth } from '../../assets/contexts/AuthContext';

/**
 * AuthorDisplay 전역 캐시
 * - 동일 uid가 여러 번 렌더되어도 네트워크 호출은 1회(in-flight Promise 공유)
 * - 너무 오래된 정보가 남지 않도록 TTL 적용
 */
const AUTHOR_USER_CACHE_TTL_MS = 60_000; // 1분(필요 시 조정)
const authorUserPromiseCache = new Map(); // uid:number -> { ts:number, promise: Promise<any> }

function getAuthorUserCached(uid) {
  const now = Date.now();
  const cached = authorUserPromiseCache.get(uid);
  if (cached && now - cached.ts < AUTHOR_USER_CACHE_TTL_MS) {
    return cached.promise;
  }
  const promise = api.get(`/api/system/user/${uid}`);
  authorUserPromiseCache.set(uid, { ts: now, promise });
  promise.catch(() => {
    // 실패한 요청은 다음 렌더에서 재시도할 수 있게 제거
    const cur = authorUserPromiseCache.get(uid);
    if (cur && cur.promise === promise) {
      authorUserPromiseCache.delete(uid);
    }
  });
  return promise;
}

/** 방장: 금색 3봉 왕관(상·하 띠 사이 간격, 둥근 실루엣) */
export const OwnerInitialBadge = () => {
  const gold = '#EBB65B';
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9.25" fill="#ffffff" stroke="rgba(235, 182, 91, 0.45)" strokeWidth="0.85" />
      {/* 상단 3봉(중앙 최고점) — 아래 y=10.62까지 채움 후 비움(간격) */}
      <path
        fill={gold}
        d="M12 4.05L9.18 8.58L6.62 6.98L7.08 10.62h9.84l.46-3.64l-2.56 1.6L12 4.05z"
      />
      {/* 하단 띠: 둥근 모서리, 참고 이미지처럼 위와 분리된 밴드 */}
      <rect x="6.88" y="12.08" width="10.24" height="3.95" rx="0.75" ry="0.75" fill={gold} />
    </svg>
  );
};

/** 접근 중지: 빨간 원 테두리 + 정중앙 빨간 사각형 */
export const AccessSuspendedInitialBadge = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="9" fill="#ffffff" stroke="#dc2626" strokeWidth="2.25" />
    <rect x="7.5" y="7.5" width="9" height="9" fill="#dc2626" />
  </svg>
);

/** 작성자 유형 코드 → 표시 라벨 (소속 미존재 시 사용). 전역 활용 가능하도록 export */
export const AUTHOR_TYPE_LABELS = {
  INDIVIDUAL: '개인회원',
  COMPANY: '기업회원',
  ADMIN: '운영자'
};
/** 작성자 유형을 알 수 없는 경우 기본 라벨 */
export const AUTHOR_TYPE_LABEL_DEFAULT = '회원';

/** formData에서 소속·기관명 추출 후보 키 (개인·연구원 등 affiliation 포함, 기업 필드명과 겹치는 항목 포함) */
const ORG_KEYS_FROM_FORM = ['institution_name', 'organization_name', 'org_name', 'affiliation', 'company_name', 'companyName', 'organizationName', 'institutionName'];

function getOrgFromFormData(formData) {
  if (!formData || typeof formData !== 'object') return '';
  const keys = ORG_KEYS_FROM_FORM;
  for (const key of keys) {
    const val = formData[key];
    if (typeof val === 'string' && val.trim() !== '') return val.trim();
  }
  return '';
}

/**
 * 범용 작성자 표시: 이니셜 + 담당자명 + (선택) ID + 소속/기관명
 * regUid 전달 시 GET /api/system/user/{uid} 호출 후 formData에서 소속·기관명 후보 키(institution_name, affiliation 등)로 표시.
 * 스타일: public/assets/css/alliance-common.css (.alli.alli-author, .alli-author-initial, .alli-author-name, .alli-author-org)
 * @param {number|string} [regUid] - 작성자 cetus_user uid. 전달 시 API 호출 후 이름·userId·org 표시
 * @param {string} [initial] - 원형 이니셜 (regUid 미사용 또는 조회 실패 시 사용)
 * @param {string} [name] - 담당자명 fallback
 * @param {string} [userId] - ID fallback
 * @param {boolean} [showUserId=true] - userId 노출 여부
 * @param {string} [org] - 소속/기관명 fallback
 * @param {string} [authorCd] - 비로그인 시 회원 유형 라벨용 (부모가 공개 API에서 전달)
 * @param {boolean} [skipUserFetch=false] - true면 /api/system/user 조회 안 함 (목록·툴팁 등에서 이미 props로 충분할 때)
 * @param {string} [className] - wrapper 추가 클래스
 * @param {React.ReactNode} [initialOverlay] - 이니셜 원 우하단 겹침 (커스텀 뱃지)
 * @param {boolean} [showOwnerIcon=false] - true면 이니셜 원 우상단에 방장(금색 왕관) 표시
 * @param {boolean} [showAccessSuspendedIcon=false] - true면 이니셜 원 우상단에 접근 중지(빨간 원·중앙 사각형) 표시
 * @param {string} [avatarUrl] - skipUserFetch 등으로 API 조회 없을 때 프로필 이미지 URL (선택)
 */
const AuthorDisplay = ({
  regUid,
  initial = '',
  name = '',
  userId = '',
  showUserId = true,
  org = '',
  authorCd: authorCdProp = '',
  skipUserFetch = false,
  className = '',
  initialOverlay = null,
  showOwnerIcon = false,
  showAccessSuspendedIcon = false,
  avatarUrl = '',
  ...props
}) => {
  const { t } = useTranslation();
  const authorTypeLabels = useMemo(() => ({
    INDIVIDUAL: t('components.authorDisplay.individual'),
    COMPANY: t('components.authorDisplay.company'),
    ADMIN: t('components.authorDisplay.admin'),
  }), [t]);
  const authorTypeDefault = t('components.authorDisplay.memberDefault');
  const { user } = useAuth();
  const [fetched, setFetched] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [verifiedAvatarSrc, setVerifiedAvatarSrc] = useState('');

  useEffect(() => {
    if (skipUserFetch) {
      setFetched(null);
      setLoadFailed(false);
      return;
    }
    const uid = regUid != null ? Number(regUid) : null;
    if (uid == null || isNaN(uid)) {
      setFetched(null);
      setLoadFailed(false);
      return;
    }
    // 비회원: /api/system/user 는 인증 필요 → 호출하지 않고 name/org 등 부모 props만 사용
    if (!user?.uid) {
      setFetched(null);
      setLoadFailed(false);
      return;
    }
    setLoadFailed(false);
    let cancelled = false;
    getAuthorUserCached(uid)
      .then((res) => {
        if (cancelled) return;
        const userInfo = res?.userInfo || res;
        const authorCd = userInfo?.authorCd ?? '';
        const formData = res?.formData || {};
        const displayName = userInfo?.userNm ?? userInfo?.userName ?? '';
        const displayId = userInfo?.userId ?? '';
        const displayOrg = (res?.org != null && String(res.org).trim() !== '')
          ? String(res.org).trim()
          : getOrgFromFormData(formData);
        const profileUid = userInfo?.profileUid;
        const profileId = userInfo?.profileId;
        setFetched({
          name: displayName,
          userId: displayId,
          org: displayOrg,
          authorCd,
          profileUid: profileUid != null ? profileUid : null,
          profileId: profileId != null && String(profileId).trim() !== '' ? String(profileId).trim() : null
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setFetched(null);
      });
    return () => {
      cancelled = true;
    };
  }, [regUid, user?.uid, skipUserFetch]);

  const useFetched = fetched != null && !loadFailed;
  const displayName = useFetched ? fetched.name : (name ?? '');
  const displayUserId = useFetched ? fetched.userId : (userId ?? '');
  const rawOrg = useFetched ? fetched.org : (org ?? '');
  const authorCd = useFetched ? (fetched.authorCd ?? '') : (authorCdProp ?? '');

  const resolvedAvatarSrc = useMemo(() => {
    const fromProp = avatarUrl != null && String(avatarUrl).trim() !== '' ? String(avatarUrl).trim() : '';
    if (fromProp) return fromProp;
    if (!useFetched || !fetched) return '';
    const pu = fetched.profileUid;
    if (pu != null && String(pu).trim() !== '') {
      return `/api/glow/files/viewByUid/${encodeURIComponent(String(pu).trim())}`;
    }
    const pid = fetched.profileId;
    if (pid != null && String(pid).trim() !== '') {
      return `/api/glow/files/view?fileId=${encodeURIComponent(String(pid).trim())}`;
    }
    return '';
  }, [avatarUrl, useFetched, fetched]);

  useEffect(() => {
    setAvatarImgError(false);
  }, [resolvedAvatarSrc]);

  // 존재하지 않는 프로필 파일로 img 404 콘솔 노이즈 방지 — 성공 시에만 src 설정
  useEffect(() => {
    if (!resolvedAvatarSrc) {
      setVerifiedAvatarSrc('');
      return undefined;
    }
    let cancelled = false;
    let objectUrl = '';
    fetch(resolvedAvatarSrc, { credentials: 'include' })
      .then((res) => (cancelled || !res.ok ? null : res.blob()))
      .then((blob) => {
        if (cancelled || !blob) {
          setVerifiedAvatarSrc('');
          return;
        }
        objectUrl = URL.createObjectURL(blob);
        setVerifiedAvatarSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setVerifiedAvatarSrc('');
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [resolvedAvatarSrc]);

  const displayInitial =
    initial != null && String(initial).trim() !== ''
      ? String(initial).trim()
      : (displayName != null && String(displayName).trim() !== '' ? String(displayName).trim().charAt(0) : '-');
  const hasName = displayName != null && String(displayName).trim() !== '';
  const hasUserId = showUserId && displayUserId != null && String(displayUserId).trim() !== '';

  // 비회원은 회사명 대신 고정 라벨로 표시, 로그인 사용자는 회사명(소속) 우선 표시
  const hasRawOrg = rawOrg != null && String(rawOrg).trim() !== '';
  const displayOrgLabel = !user?.uid
    ? authorTypeDefault
    : hasRawOrg
      ? String(rawOrg).trim()
      : (authorTypeLabels[String(authorCd).toUpperCase()] ?? authorTypeDefault);

  const showAvatarImg = Boolean(verifiedAvatarSrc && !avatarImgError);

  return (
    <div
      className={`author-display alli alli-author${className ? ` ${className}` : ''}`}
      {...props}
    >
      <span
        className={`alli alli-author-initial${
          initialOverlay ? ' alli-author-initial--has-overlay' : ''
        }${showAccessSuspendedIcon ? ' alli-author-initial--access-suspended' : ''}${
          showOwnerIcon ? ' alli-author-initial--owner-badge' : ''
        }${showAvatarImg ? ' alli-author-initial--has-avatar' : ''}`}
        aria-hidden={
          showAccessSuspendedIcon || showOwnerIcon ? undefined : 'true'
        }
      >
        {showAvatarImg ? (
          <img
            src={verifiedAvatarSrc}
            alt=""
            className="alli-author-initial-avatar"
            onError={() => setAvatarImgError(true)}
          />
        ) : (
          displayInitial
        )}
        {showOwnerIcon ? (
          <span
            className={`alli-author-initial-owner${
              showAccessSuspendedIcon ? ' alli-author-initial-owner--paired' : ''
            }`}
            title={t('components.authorDisplay.hostTitle')}
            role="img"
            aria-label={t('components.authorDisplay.host')}
          >
            <OwnerInitialBadge />
          </span>
        ) : null}
        {showAccessSuspendedIcon ? (
          <span
            className="alli-author-initial-access-suspended"
            title={t('components.authorDisplay.accessSuspendedTitle')}
            role="img"
            aria-label={t('components.authorDisplay.accessSuspended')}
          >
            <AccessSuspendedInitialBadge />
          </span>
        ) : null}
        {initialOverlay != null && (
          <span className="alli-author-initial-overlay">{initialOverlay}</span>
        )}
      </span>
      {hasName ? <span className="alli alli-author-name">{displayName}</span> : null}
      {hasUserId ? (
        <span className="alli alli-author-user-id">({String(displayUserId).trim()})</span>
      ) : null}
      <span className="alli alli-author-org">{displayOrgLabel}</span>
    </div>
  );
};

export default AuthorDisplay;
